import React from 'react';
import {
  Box, Typography, RadioGroup, FormControlLabel, Radio, Slider,
  Checkbox, Button, Snackbar, Alert, Paper, Grid,
} from '@mui/material';
import DashboardLayout from '../../../shared/layout/DashboardLayout';
import { useParams } from 'react-router-dom';
import { useCuestionarioSPForm } from './hooks/useCuestionarioSPForm';

const FORM_COLORS = {
  primary: '#0d6aee',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#dbe7f3',
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 3,
  border: `1px solid ${FORM_COLORS.border}`,
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const OPCIONES_FRECUENCIA = [
  'Nunca',
  'Una vez a la semana',
  '2-3 veces/semana',
  'Una vez al día',
  'Varias veces al día',
  'Continuamente',
];

const OPCIONES_CANTIDAD = [
  'No se me escapa nada',
  'Muy poca cantidad',
  'Una cantidad moderada',
  'Mucha cantidad',
];

const OPCIONES_FACTORES = [
  'Nunca',
  'Antes de llegar al servicio',
  'Al toser o estornudar',
  'Mientras duerme',
  'Al realizar esfuerzos físicos/ejercicio',
  'Cuando termina de orinar y ya se ha vestido',
  'Sin motivo evidente',
  'De forma continua',
];

const CuestionarioIncontinencia = () => {
  const { idPaciente, idCuestionario } = useParams();
  const {
    frecuencia, setFrecuencia,
    cantidad, setCantidad,
    impactoVida, setImpactoVida,
    factores, handleCheckboxChange,
    openGood, setOpenGood,
    guardarDatos,
  } = useCuestionarioSPForm({ idPaciente, idCuestionario });

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 3 }}>
        <Paper
          elevation={0}
          sx={{ width: '100%', maxWidth: 800, p: { xs: 2, md: 4 }, borderRadius: 4, border: `1px solid ${FORM_COLORS.border}` }}
        >
          <Typography variant="h4" sx={{ textAlign: 'center', color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
            Cuestionario de Incontinencia Urinaria
          </Typography>
          <Typography sx={{ textAlign: 'center', color: FORM_COLORS.textMuted, mb: 4 }}>
            ICIQ-SF — Valoración del impacto de la incontinencia en la calidad de vida.
          </Typography>

          {/* Pregunta 1 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
              1. ¿Con qué frecuencia pierde orina?
            </Typography>
            <RadioGroup value={frecuencia} onChange={(e) => setFrecuencia(e.target.value)}>
              {OPCIONES_FRECUENCIA.map((opcion, index) => (
                <FormControlLabel
                  key={index}
                  value={index.toString()}
                  control={<Radio sx={{ color: FORM_COLORS.primary, '&.Mui-checked': { color: FORM_COLORS.primary } }} />}
                  label={<Typography sx={{ color: FORM_COLORS.textMain }}>{opcion}</Typography>}
                />
              ))}
            </RadioGroup>
          </Paper>

          {/* Pregunta 2 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
              2. Indique la cantidad de orina que cree que se le escapa.
            </Typography>
            <RadioGroup value={cantidad} onChange={(e) => setCantidad(e.target.value)}>
              {OPCIONES_CANTIDAD.map((opcion, index) => (
                <FormControlLabel
                  key={index}
                  value={index.toString()}
                  control={<Radio sx={{ color: FORM_COLORS.primary, '&.Mui-checked': { color: FORM_COLORS.primary } }} />}
                  label={<Typography sx={{ color: FORM_COLORS.textMain }}>{opcion}</Typography>}
                />
              ))}
            </RadioGroup>
          </Paper>

          {/* Pregunta 3 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
              3. ¿En qué medida estos escapes han afectado su vida diaria?
            </Typography>
            <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted, mb: 2 }}>
              Valor seleccionado: <strong>{impactoVida}</strong> / 10
            </Typography>
            <Slider
              value={impactoVida}
              onChange={(_, newValue) => setImpactoVida(newValue)}
              step={1}
              marks
              min={1}
              max={10}
              valueLabelDisplay="auto"
              sx={{ color: FORM_COLORS.primary }}
            />
          </Paper>

          {/* Pregunta 4 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
              4. ¿Cuándo pierde orina? (Marque todas las que correspondan)
            </Typography>
            <Grid container>
              {OPCIONES_FACTORES.map((opcion, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value={opcion}
                        checked={factores.includes(opcion)}
                        onChange={handleCheckboxChange}
                        sx={{ color: FORM_COLORS.primary, '&.Mui-checked': { color: FORM_COLORS.primary } }}
                      />
                    }
                    label={<Typography sx={{ color: FORM_COLORS.textMain }}>{opcion}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={guardarDatos} sx={{ borderRadius: 2.5, px: 4 }}>
              Guardar respuestas
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
        <Alert severity="success" onClose={() => setOpenGood(false)}>
          ¡Respuestas guardadas correctamente!
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default CuestionarioIncontinencia;
